import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { LlmChatSlashCommandKind, type LlmChatSlashCommandDto } from '@paladin-monorepo/common';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { llmChatApi } from '@/lib/api/llm-chat';
import { filterSlashCommands } from '@/lib/llm-chat-slash-commands';
import { cn } from '@/lib/utils';

interface LlmChatSlashCommandPickerProps {
  sessionId: string;
  /** Text typed after the `/`. */
  query: string;
  onSelect: (command: LlmChatSlashCommandDto) => void;
  onClose: () => void;
  /** Raised so the composer can route Up/Down/Enter into the list. */
  registerKeyHandler: (handler: ((event: KeyboardEvent) => boolean) | null) => void;
}

export function LlmChatSlashCommandPicker({
  sessionId,
  query,
  onSelect,
  onClose,
  registerKeyHandler,
}: LlmChatSlashCommandPickerProps) {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);

  const commandsQuery = useQuery({
    queryKey: ['llm-chat-slash-commands', sessionId],
    queryFn: () => llmChatApi.getSlashCommands(sessionId),
    staleTime: 5 * 60_000,
  });

  const rows = useMemo(
    () => filterSlashCommands(commandsQuery.data ?? [], query),
    [commandsQuery.data, query],
  );

  const resetKey = `${query}|${rows.length}`;
  const [lastResetKey, setLastResetKey] = useState(resetKey);
  if (lastResetKey !== resetKey) {
    setLastResetKey(resetKey);
    setActiveIndex(0);
  }

  useEffect(() => {
    const handler = (event: KeyboardEvent): boolean => {
      if (event.key === 'Escape') {
        onClose();
        return true;
      }
      if (rows.length === 0) return false;

      if (event.key === 'ArrowDown') {
        setActiveIndex((index) => (index + 1) % rows.length);
        return true;
      }
      if (event.key === 'ArrowUp') {
        setActiveIndex((index) => (index - 1 + rows.length) % rows.length);
        return true;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        onSelect(rows[activeIndex]);
        return true;
      }
      return false;
    };

    registerKeyHandler(handler);
    return () => registerKeyHandler(null);
  }, [activeIndex, onClose, onSelect, registerKeyHandler, rows]);

  return (
    <div
      className="absolute bottom-full left-0 z-50 mb-2 w-full max-w-md overflow-hidden rounded-lg border bg-popover shadow-md"
      // Keeps focus in the composer while a row is clicked, as in the mention picker.
      onMouseDown={(event) => event.preventDefault()}
    >
      <Command shouldFilter={false} className="max-h-72">
        <CommandList>
          {commandsQuery.isError && (
            <div className="px-3 py-2 text-sm text-destructive">{t('llmChat.slashCommands.loadFailed')}</div>
          )}

          {commandsQuery.isPending && (
            <div className="px-3 py-2 text-sm text-muted-foreground">{t('llmChat.slashCommands.loading')}</div>
          )}

          {rows.length > 0 && (
            <CommandGroup>
              {rows.map((command, index) => (
                <CommandItem
                  key={command.name}
                  value={`slash-${command.name}`}
                  onSelect={() => onSelect(command)}
                  className={cn(
                    'flex cursor-pointer flex-col items-start gap-0.5',
                    index === activeIndex && 'bg-accent',
                  )}
                >
                  <span className="flex w-full items-center justify-between gap-2">
                    <span className="font-medium">/{command.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {t(`llmChat.slashCommands.kinds.${command.kind}`)}
                    </span>
                  </span>
                  <span className="line-clamp-2 text-xs text-muted-foreground">
                    {command.kind === LlmChatSlashCommandKind.Builtin
                      ? t(`llmChat.slashCommands.builtin.${command.name}`, { defaultValue: '' })
                      : command.description}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {commandsQuery.isSuccess && rows.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">{t('llmChat.slashCommands.noMatches')}</div>
          )}
        </CommandList>
      </Command>
    </div>
  );
}
